import { TEMPLATE_LANGUAGES } from '../../../types/customEmailTemplate';
import type { CustomEmailTemplateContent, CustomEmailTemplateSetting, TemplateLanguage } from '../../../types/customEmailTemplate';
import { EMAIL_SUBJECT_MAX_LENGTH } from '../../../types/customerFollowUp/email';
import { toCanonical } from './customTemplateMarkers';

export const MAX_SUBJECT_LENGTH = EMAIL_SUBJECT_MAX_LENGTH;
/** Matches the server limit on a reminder draft's body, which the template body becomes. */
export const MAX_BODY_LENGTH = 4096;

export type FieldError = 'required' | 'tooLong';

export interface LanguageErrors {
    subject?: FieldError;
    body?: FieldError;
}

export type DraftsByLanguage = Record<TemplateLanguage, CustomEmailTemplateContent>;

export interface ValidationResult {
    setting: CustomEmailTemplateSetting | null;
    errors: Partial<Record<TemplateLanguage, LanguageErrors>>;
    noLanguage: boolean;
}

const fieldError = (value: string, max: number, displayedLength = value.length): FieldError | undefined => {
    if (!value.trim()) return 'required';
    return Math.max(value.length, displayedLength) > max ? 'tooLong' : undefined;
};

/** A language left entirely blank is simply not written; one filled halfway is an error. */
export const validateDrafts = (drafts: DraftsByLanguage): ValidationResult => {
    const errors: ValidationResult['errors'] = {};
    const contents: Partial<Record<TemplateLanguage, CustomEmailTemplateContent>> = {};

    for (const language of TEMPLATE_LANGUAGES) {
        const subject = toCanonical(drafts[language].subject).trim();
        const body = toCanonical(drafts[language].body);
        if (!subject && !body.trim()) continue;

        const languageErrors: LanguageErrors = {
            // The user counts what the field shows; the server counts the stored form. Both must fit.
            subject: fieldError(subject, MAX_SUBJECT_LENGTH, drafts[language].subject.trim().length),
            body: fieldError(body, MAX_BODY_LENGTH),
        };
        if (languageErrors.subject || languageErrors.body) errors[language] = languageErrors;
        else contents[language] = { subject, body };
    }

    const noLanguage = !contents.FR && !contents.EN && Object.keys(errors).length === 0;
    const valid = !noLanguage && Object.keys(errors).length === 0;
    return {
        setting: valid ? { fr: contents.FR ?? null, en: contents.EN ?? null } : null,
        errors,
        noLanguage,
    };
};
